import { useLang } from '../../i18n/LangContext';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  atHomeMe,
  companionMe,
  companionMemory,
  companionMemoryForget,
  companionMemoryRestore,
  companionUnsafe,
  type CompanionMemoryFact,
  type CompanionUnsafeSignal,
  greet,
} from '@aithora/shared';
import { useSession } from '../../session/SessionContext';
import { A, clockOrDay, firstName, st, useApi } from './ui';
import { sendChat } from './chat';

// Atlas for an Aithora Home member (design T377-495, isConsumerChat): at home nobody
// is down the hall, so the page opens with how long it would take someone to know. The
// chat is the real Atlas (POST /chat); what it remembers and what it raised come from
// the routes; "I don't feel safe" opens a real Guardian signal.

const ZOOM = [1, 1.14, 1.28];
const CHIPS = ['How am I doing?', 'Log my weight', 'Bigger text', 'Call my person', 'My benefits'];

type Told = { who: string; what: string };

export function MemberCompanion() {
  const { client, session } = useSession();
  const { lang } = useLang();
  const navigate = useNavigate();
  const memberId = session?.user.id ?? '';
  const orgId = session?.user.organizationId ?? '';
  const home = useApi((c, signal) => atHomeMe(c, { params: { id: memberId }, signal }), [memberId], !!memberId);
  const me = useApi((c, signal) => companionMe(c, { signal }));
  const mem = useApi((c, signal) => companionMemory(c, { params: { id: memberId }, signal }), [memberId], !!memberId);
  const [size, setSize] = useState(0);
  const [voice, setVoice] = useState(false);
  const [unsafe, setUnsafe] = useState<{ told: Told[]; ok: boolean } | null>(null);
  const [input, setInput] = useState('');
  const [msgs, setMsgs] = useState<{ who: 'me' | 'bot'; text: string }[]>([]);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [last, setLast] = useState<CompanionMemoryFact | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const h = home.data;
  const name = firstName(h?.name ?? session?.user.name);
  const person = h?.primaryContact ? firstName(h.primaryContact) : null;
  const within = me.data?.familyWithinMinutes ?? null;
  const hiLine = `Hi ${name}. I am Atlas. Ask me how you are doing, or tap a button below.`;
  const shown = [{ who: 'bot' as const, text: hiLine }, ...msgs];

  const watchLine = (() => {
    if (!h) return '';
    const spoke = h.lastCheckInMinutes === null ? 'We have not spoken yet.' : `We spoke ${h.lastCheckInMinutes} minutes ago.`;
    if (!person || within === null) return spoke;
    return `${spoke} If you needed help and could not reach the phone, ${person} would hear from us within ${within} minutes, and the care line straight after.`;
  })();

  const send = async (text: string) => {
    const t = text.trim();
    if (!t || !memberId) return;
    setErr(null);
    if (t === 'Bigger text') {
      setSize((s) => Math.min(2, s + 1));
      setMsgs((p) => [...p, { who: 'me', text: t }, { who: 'bot', text: 'Made the text bigger. Say it again if you want it bigger still.' }]);
      return;
    }
    if (t === 'My benefits') {
      navigate('/home');
      return;
    }
    setMsgs((p) => [...p, { who: 'me', text: t }]);
    try {
      const r = await sendChat(client, { message: t, userId: memberId, organizationId: orgId, conversationId });
      if (r.conversationId) setConversationId(r.conversationId);
      setMsgs((p) => [...p, { who: 'bot', text: r.text }]);
    } catch (e) {
      setErr((e as Error).message || 'I could not answer just now.');
    }
  };
  const feelUnsafe = async () => {
    setErr(null);
    try {
      const r = (await companionUnsafe(client, { body: {} })) as CompanionUnsafeSignal & { told?: Told[] };
      setUnsafe({ told: Array.isArray(r.told) ? r.told : [], ok: false });
    } catch (e) {
      setErr((e as Error).message || 'That did not go through. Call 911 if you are in danger.');
    }
  };
  const forget = async (f: CompanionMemoryFact) => {
    setErr(null);
    try {
      await companionMemoryForget(client, { params: { id: memberId, factId: f.id } });
      mem.setData((p) => (p ? { ...p, facts: p.facts.filter((x) => x.id !== f.id) } : p));
      setLast(f);
    } catch (e) {
      setErr((e as Error).message || 'I could not forget that just now.');
    }
  };
  const undo = async () => {
    if (!last) return;
    try {
      await companionMemoryRestore(client, { params: { id: memberId, factId: last.id } });
      setLast(null);
      mem.reload();
    } catch (e) {
      setErr((e as Error).message || 'I could not put it back just now.');
    }
  };
  const raised = me.data?.raised ?? [];
  const who = person ?? 'your family';

  return (
    <div data-testid="companion-member" style={st(`zoom:${ZOOM[size]};box-sizing:border-box;max-width:720px;display:flex;flex-direction:column;gap:18px;min-width:0;`)}>
      <div style={st('display:flex;align-items:center;gap:12px;flex-wrap:wrap;row-gap:10px;min-width:0;')}>
        <span style={st('flex:0 0 auto;font-size:15px;font-weight:700;color:#22272E;')}>Text size</span>
        {[0, 1, 2].map((i) => (
          <A
            key={i}
            label={['Normal', 'Large', 'Largest'][i] + ' text'}
            selected={size === i}
            onClick={() => setSize(i)}
            s={`display:inline-flex;align-items:center;justify-content:center;width:48px;height:48px;border-radius:10px;text-decoration:none;font-weight:700;box-sizing:border-box;font-size:${[15, 19, 24][i]}px;${size === i ? 'background:#1D3F6B;color:#fff;' : 'background:rgba(34,39,46,.07);color:#22272E;'}`}
          >
            A
          </A>
        ))}
      </div>

      <div style={st('min-width:0;')}>
        <div style={st('font-size:23px;font-weight:700;letter-spacing:-.024em;line-height:1.25;text-wrap:pretty;')}>{`${greet(name, new Date(), session?.user.timeZone ?? null, lang === 'es' ? 'es' : 'en')}.`}</div>
        <div style={st('font-size:15px;color:#3C454F;line-height:1.55;margin-top:5px;max-width:60ch;text-wrap:pretty;')}>{watchLine}</div>
      </div>

      <div style={st('display:flex;align-items:center;gap:14px;flex-wrap:wrap;row-gap:10px;min-width:0;')}>
        <A onClick={() => setVoice(!voice)} selected={voice} s={`display:inline-flex;align-items:center;gap:12px;min-height:60px;padding:0 26px;border-radius:12px;text-decoration:none;font-size:19px;font-weight:700;white-space:nowrap;color:#fff;background:${voice ? '#1F6B52' : '#1D3F6B'};`}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={st('flex:0 0 24px;')} aria-hidden="true">
            <path d="M12 3.5a3 3 0 0 1 3 3v5a3 3 0 0 1-6 0v-5a3 3 0 0 1 3-3Z" />
            <path d="M5.5 11a6.5 6.5 0 0 0 13 0" />
            <path d="M12 17.5v3" />
          </svg>
          {voice ? 'Listening. Say anything.' : 'Talk to me'}
          {voice && (
            <span style={st('display:inline-flex;align-items:flex-end;gap:3px;height:18px;')}>
              <span style={st('width:4px;border-radius:2px;background:currentColor;animation:vu 1s ease-in-out infinite;height:8px;')} />
              <span style={st('width:4px;border-radius:2px;background:currentColor;animation:vu 1s ease-in-out .18s infinite;height:15px;')} />
              <span style={st('width:4px;border-radius:2px;background:currentColor;animation:vu 1s ease-in-out .36s infinite;height:11px;')} />
            </span>
          )}
        </A>
        <span style={st('flex:1 1 200px;min-width:0;font-size:14px;color:#54606C;line-height:1.45;text-wrap:pretty;')}>
          {voice ? 'Take your time. Tap again when you are done.' : 'Tap here, or say “Hey Siri, talk to Atlas” on your phone, tablet or computer. I will answer out loud.'}
        </span>
      </div>
      <div style={st('font-size:19px;line-height:1.5;color:#22272E;text-wrap:pretty;')}>
        <b style={st('font-weight:700;')}>Try saying:</b>
        {` “What’s on my day?” · “I took my pills” · “Call ${person ?? 'my family'}” · “I need help”`}
      </div>

      {!unsafe && (
        <div style={st('display:flex;align-items:center;gap:12px 16px;flex-wrap:wrap;min-width:0;')}>
          <A write onClick={() => void feelUnsafe()} s="flex:0 0 auto;display:inline-flex;align-items:center;min-height:60px;padding:0 24px;border-radius:12px;font-size:19px;font-weight:700;text-decoration:none;white-space:nowrap;color:#8A1C16;background:#FCFAF4;border:2px solid rgba(179,38,30,.35);box-sizing:border-box;">
            I don’t feel safe
          </A>
          <span style={st('flex:1 1 220px;min-width:0;font-size:15px;color:#3C454F;line-height:1.45;text-wrap:pretty;')}>
            {`Press it and ${person ?? 'your family'} and the Aithora care line are told straight away. You do not have to say why.`}
          </span>
        </div>
      )}
      {unsafe && (
        <div role="status" style={st('display:flex;flex-direction:column;gap:14px;padding:20px 22px;border-radius:16px;background:#FFF4F2;border:2px solid rgba(179,38,30,.4);min-width:0;')}>
          <div style={st('font-size:23px;font-weight:800;line-height:1.3;color:#22272E;')}>You are not on your own. Help is on its way.</div>
          {unsafe.told.length > 0 && (
            <div style={st('display:flex;flex-direction:column;min-width:0;')}>
              {unsafe.told.map((u) => (
                <div key={u.who} style={st('display:flex;align-items:baseline;gap:6px 14px;flex-wrap:wrap;padding:11px 0;border-top:1px solid rgba(179,38,30,.18);min-width:0;')}>
                  <span style={st('flex:0 0 auto;font-size:19px;font-weight:700;color:#22272E;')}>{u.who}</span>
                  <span style={st('flex:1 1 220px;min-width:0;font-size:17px;color:#3C454F;line-height:1.45;text-wrap:pretty;')}>{u.what}</span>
                </div>
              ))}
            </div>
          )}
          <div style={st('display:flex;align-items:center;gap:10px 16px;flex-wrap:wrap;min-width:0;')}>
            <a href="tel:911" style={st('flex:0 0 auto;display:inline-flex;align-items:center;min-height:64px;padding:0 30px;border-radius:12px;font-size:21px;font-weight:800;text-decoration:none;white-space:nowrap;color:#fff;background:#B3261E;')}>
              Call 911
            </a>
            <span style={st('flex:1 1 220px;min-width:0;font-size:17px;color:#22272E;line-height:1.45;text-wrap:pretty;')}>If you are in danger right now, call 911. You do not have to explain anything to us first.</span>
          </div>
          <div style={st('font-size:16px;color:#3C454F;line-height:1.5;text-wrap:pretty;')}>
            If you cannot talk freely, you do not have to. The care line will send a text before it calls, and will not ring out loud unless you answer.
          </div>
          <A onClick={() => setUnsafe({ ...unsafe, ok: true })} s="align-self:flex-start;display:inline-flex;align-items:center;min-height:60px;padding:0 22px;border-radius:12px;font-size:18px;font-weight:700;text-decoration:none;color:#22272E;background:#fff;border:2px solid rgba(34,39,46,.22);box-sizing:border-box;">
            {unsafe.ok ? 'Thank you. We will still check on you today.' : 'I am safe now'}
          </A>
        </div>
      )}

      {raised.length > 0 && (
        <div style={st('min-width:0;')}>
          <div style={st('font-size:14px;font-weight:700;letter-spacing:.09em;text-transform:uppercase;color:#54606C;padding-bottom:4px;')}>It brought these up with you today</div>
          {raised.map((r, i) => (
            <div key={i} style={st('display:flex;align-items:baseline;gap:12px;flex-wrap:wrap;row-gap:4px;padding:10px 0;border-top:1px solid rgba(34,39,46,.14);min-width:0;')}>
              <span style={st('flex:1 1 200px;min-width:0;font-size:15.5px;color:#22272E;line-height:1.45;text-wrap:pretty;')}>{r.what}</span>
              <span style={st('flex:0 0 auto;font-size:13px;color:#54606C;white-space:nowrap;')}>{clockOrDay(r.at)}</span>
            </div>
          ))}
          <div style={st('font-size:13px;color:#54606C;line-height:1.5;padding-top:8px;max-width:66ch;text-wrap:pretty;')}>
            I start these, you do not have to remember to ask. If you would rather I did not, say so and I will stop.
          </div>
        </div>
      )}

      <div style={st('display:flex;flex-direction:column;min-width:0;height:420px;border-top:1px solid rgba(34,39,46,.16);')}>
        <div style={st('flex:1;min-height:0;overflow:auto;padding:14px 0 6px;display:flex;flex-direction:column;gap:12px;')}>
          {shown.map((m, i) => (
            <div key={i} style={st('display:flex;')}>
              <div
                style={st(
                  m.who === 'me'
                    ? 'max-width:80%;margin-left:auto;padding:10px 14px;border-radius:14px 14px 4px 14px;background:#2F5C97;color:#fff;font-size:16px;line-height:1.45;'
                    : 'max-width:80%;padding:10px 14px;border-radius:14px 14px 14px 4px;background:#F3EFE5;color:#22272E;font-size:16px;line-height:1.45;',
                )}
              >
                {m.text}
              </div>
            </div>
          ))}
          {err && <div role="alert" style={st('font-size:15px;color:#8A1C16;line-height:1.45;')}>{err}</div>}
        </div>
        <div style={st('display:flex;align-items:center;gap:8px;flex-wrap:wrap;padding:12px 0 0;border-top:1px solid rgba(34,39,46,.12);')}>
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                const t = input;
                setInput('');
                void send(t);
              }
            }}
            placeholder="Type here, or tap a button below"
            aria-label="Type to Atlas"
            style={st('flex:1 1 200px;min-width:180px;height:48px;padding:0 14px;border:1px solid rgba(34,39,46,.18);border-radius:10px;background:#FBF8F1;font-size:16px;color:#22272E;font-family:inherit;box-sizing:border-box;')}
          />
          <A
            write
            onClick={() => {
              const t = input;
              setInput('');
              void send(t);
            }}
            s="flex:0 0 auto;display:inline-flex;align-items:center;justify-content:center;height:48px;padding:0 20px;border-radius:10px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;color:#FCFAF4;background:#2F5C97;"
          >
            Send
          </A>
        </div>
      </div>

      <div style={st('display:flex;flex-wrap:wrap;gap:9px;')}>
        {CHIPS.map((label) => (
          <A key={label} write onClick={() => void send(label)} s="display:inline-flex;align-items:center;min-height:48px;padding:0 18px;border-radius:24px;background:rgba(47,92,151,.10);color:#1D3F6B;font-size:15px;font-weight:600;text-decoration:none;white-space:nowrap;">
            {label}
          </A>
        ))}
      </div>

      <div style={st('min-width:0;')}>
        <div style={st('font-size:14px;font-weight:700;letter-spacing:.09em;text-transform:uppercase;color:#54606C;padding-bottom:4px;')}>What it remembers about you</div>
        {mem.data && mem.data.facts.length === 0 && (
          <div style={st('padding:11px 0;border-top:1px solid rgba(34,39,46,.14);font-size:14.5px;color:#54606C;line-height:1.45;')}>
            Nothing remembered yet. It will add what you tell it, and you can ask it to forget anything.
          </div>
        )}
        {(mem.data?.facts ?? []).map((m) => (
          <div key={m.id} style={st('display:flex;align-items:baseline;gap:12px;flex-wrap:wrap;row-gap:4px;padding:11px 0;border-top:1px solid rgba(34,39,46,.14);min-width:0;')}>
            <span style={st('flex:1 1 180px;min-width:0;')}>
              <span style={st('display:block;font-size:16px;font-weight:700;line-height:1.3;')}>{m.name}</span>
              <span style={st('display:block;font-size:14.5px;color:#54606C;line-height:1.45;margin-top:2px;text-wrap:pretty;')}>{m.detail}</span>
            </span>
            <A write onClick={() => void forget(m)} s="flex:0 0 auto;display:inline-flex;align-items:center;min-height:44px;padding:0 14px;border-radius:9px;font-size:15px;font-weight:700;text-decoration:none;white-space:nowrap;color:#8A1D17;background:rgba(179,38,30,.10);">
              Forget this
            </A>
          </div>
        ))}
        {last && (
          <div style={st('display:flex;align-items:center;gap:14px;flex-wrap:wrap;row-gap:8px;margin-top:12px;padding:13px 15px;border-radius:11px;background:rgba(47,138,111,.10);')}>
            <span style={st('flex:1 1 200px;min-width:0;font-size:15.5px;color:#12503C;line-height:1.4;text-wrap:pretty;')}>{`I have forgotten this: ${last.name}.`}</span>
            <A write onClick={() => void undo()} s="flex:0 0 auto;display:inline-flex;align-items:center;min-height:44px;padding:0 17px;border-radius:9px;font-size:15px;font-weight:700;text-decoration:none;white-space:nowrap;color:#fff;background:#1F6B52;">
              Put it back
            </A>
            <A onClick={() => setLast(null)} s="flex:0 0 auto;display:inline-flex;align-items:center;min-height:44px;padding:0 15px;border-radius:9px;font-size:15px;font-weight:600;text-decoration:none;white-space:nowrap;color:#3C454F;background:rgba(34,39,46,.07);">
              That is right
            </A>
          </div>
        )}
      </div>

      <div style={st('min-width:0;')}>
        {(
          [
            ['If you ask me about a dose or a symptom', `I will not answer it myself. I will say so, and offer to get ${who === 'your family' ? 'your family' : who} or the nurse on the phone right then.`],
            ['I will keep nudging you towards people', 'I am company, not a substitute for it. When I notice you have not seen anyone in a while, I will say so.'],
            ['If you change your mind', 'Anything you ask me to forget can be put straight back.'],
          ] as const
        ).map(([k, v]) => (
          <div key={k} style={st('padding:12px 0;border-top:1px solid rgba(34,39,46,.14);min-width:0;')}>
            <span style={st('display:block;font-size:16px;font-weight:700;line-height:1.35;text-wrap:pretty;')}>{k}</span>
            <span style={st('display:block;font-size:15px;color:#3C454F;line-height:1.5;margin-top:3px;text-wrap:pretty;')}>{v}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
